import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft, CalendarDays } from "lucide-react";
import { AppSidebar } from "@/components/AppSidebar";
import { MobileTopBar } from "@/components/MobileTopBar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { SingleMonthPicker } from "@/components/MonthRangePicker";
import { MultiSelect } from "@/components/GenericFilterBar";
import { RecoveryTablesPanel } from "@/components/RecoveryTablesPanel";
import { fetchRecoveryDateRange, fetchRecoveryFilterOptions, type RecoveryFilters } from "@/analytics/recovery.api";
import {
  downloadRecoveryDrillThroughExcel,
  type RecoveryExportOption,
} from "@/analytics/recoveryDrillThrough";
import {
  DrillThroughExportControl,
  type DrillThroughExportOption,
} from "@/components/DrillThroughExportControl";
import { useToast } from "@/hooks/use-toast";

type BaseFilters = Pick<RecoveryFilters, "managers" | "client_partners" | "assignment_names" | "service_lines" | "legacy_location">;

type LocationState = {
  baseFilters?: BaseFilters;
  month?: string | null;
} | null;

const EMPTY_BASE: BaseFilters = {
  managers: [],
  client_partners: [],
  assignment_names: [],
  service_lines: [],
  legacy_location: [],
};

const EXPORT_OPTIONS: readonly DrillThroughExportOption<RecoveryExportOption>[] = [
  { value: "kpi_by_office", label: "KPI by Office" },
  { value: "kpi_by_service_line", label: "KPI by Service Line" },
  { value: "client_hierarchy", label: "Client Hierarchy" },
];

function formatMonthLabel(iso: string | null): string {
  if (!iso) return "";
  const [, y, m] = iso.split("T")[0].split("-");
  const names = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return `${names[Number(m) - 1]} ${y}`;
}

export default function RecoveryTablesPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { state } = useLocation();
  const locationState = state as LocationState;

  const baseFilters = useMemo<BaseFilters>(
    () => locationState?.baseFilters ?? EMPTY_BASE,
    [locationState?.baseFilters],
  );
  const [selectedMonth, setSelectedMonth] = useState<string | null>(locationState?.month ?? null);
  const [dateRange, setDateRange] = useState<{ min: string | null; max: string | null }>({
    min: null,
    max: null,
  });
  const [isExporting, setIsExporting] = useState(false);
  const [exportOptions, setExportOptions] = useState<RecoveryExportOption[]>([]);

  // Seeded once from the incoming navigation state (e.g. a chart drill-through
  // link with a service line already applied), then fully owned by the user.
  const [serviceLines, setServiceLines] = useState<string[]>(() => baseFilters.service_lines);
  const [serviceLineOptions, setServiceLineOptions] = useState<string[]>([]);

  useEffect(() => {
    fetchRecoveryDateRange()
      .then((dr) => setDateRange({ min: dr.min_month, max: dr.max_month }))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    fetchRecoveryFilterOptions(undefined, controller.signal)
      .then((options) => setServiceLineOptions(options.service_lines))
      .catch(() => {});
    return () => controller.abort();
  }, []);

  const toggleServiceLine = (value: string) => {
    setServiceLines((current) =>
      current.includes(value) ? current.filter((v) => v !== value) : [...current, value],
    );
  };

  // Export option changes re-render this page. Keep the table filters referentially
  // stable so those UI-only updates cannot trigger the table fetch effects.
  const effectiveFilters = useMemo<RecoveryFilters | null>(() => selectedMonth
    ? {
        ...baseFilters,
        service_lines: serviceLines,
        date_from: selectedMonth,
        date_to: selectedMonth,
      }
    : null, [baseFilters, serviceLines, selectedMonth]);

  const activeFilterCount =
    baseFilters.managers.length +
    baseFilters.client_partners.length +
    baseFilters.assignment_names.length +
    baseFilters.service_lines.length +
    baseFilters.legacy_location.length;

  const toggleExportOption = (option: RecoveryExportOption) => {
    setExportOptions((current) => current.includes(option)
      ? current.filter((value) => value !== option)
      : [...current, option]);
  };

  const handleExport = async () => {
    if (!effectiveFilters || isExporting || exportOptions.length === 0) return;

    setIsExporting(true);
    try {
      await downloadRecoveryDrillThroughExcel(effectiveFilters, exportOptions);
    } catch (caught) {
      toast({
        variant: "destructive",
        title: "Couldn’t export Recovery drill-through",
        description: caught instanceof Error ? caught.message : "Failed to export Excel file",
      });
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <SidebarProvider>
      <div className="min-h-dvh flex w-full bg-background">
        <AppSidebar />

        <div className="flex flex-1 flex-col min-w-0 h-dvh overflow-hidden">
          <MobileTopBar title="Recovery Tables" />
          <main className="flex-1 overflow-auto p-2 sm:p-3 space-y-4">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border bg-card px-4 py-3 shadow-sm">
              <div>
                <h1 className="text-lg font-semibold text-foreground">Recovery Tables</h1>
                <p className="text-xs text-muted-foreground">
                  {activeFilterCount > 0
                    ? `${activeFilterCount} filter${activeFilterCount > 1 ? "s" : ""} applied from Recovery dashboard`
                    : "No dashboard filters applied"}
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <MultiSelect
                  label="Service Line"
                  options={[...new Set([...serviceLineOptions, ...serviceLines])]}
                  availableOptions={serviceLineOptions}
                  selected={serviceLines}
                  onToggle={toggleServiceLine}
                />

                <div className="h-5 w-px bg-border shrink-0" />

                {/* Month picker */}
                <div className="flex items-center gap-2">
                  <CalendarDays className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="text-sm text-muted-foreground whitespace-nowrap">Viewing month:</span>
                  <SingleMonthPicker
                    label="Select month"
                    value={selectedMonth}
                    min_month={dateRange.min}
                    max_month={dateRange.max}
                    onSelect={setSelectedMonth}
                    onClear={() => setSelectedMonth(null)}
                  />
                </div>

                <div className="h-5 w-px bg-border shrink-0" />

                <div className="flex flex-wrap items-center gap-2">
                  <DrillThroughExportControl
                    options={EXPORT_OPTIONS}
                    selectedOptions={exportOptions}
                    onToggleOption={toggleExportOption}
                    onDownload={handleExport}
                    isExporting={isExporting}
                    disabled={!effectiveFilters}
                  />
                </div>

                <Button variant="outline" size="sm" className="gap-2" onClick={() => navigate(-1)}>
                  <ArrowLeft className="h-4 w-4" />
                  Back to Recovery
                </Button>
              </div>
            </div>

            {/* Content */}
            {effectiveFilters ? (
              <RecoveryTablesPanel filters={effectiveFilters} />
            ) : (
              <div className="flex flex-col items-center justify-center rounded-lg border bg-card py-24 text-center shadow-sm gap-3">
                <CalendarDays className="h-10 w-10 text-muted-foreground/50" />
                <p className="text-sm font-medium text-foreground">No month selected</p>
                <p className="text-xs text-muted-foreground max-w-xs">
                  Use the month picker above to select which month you want to drill into.
                </p>
              </div>
            )}
          </main>
        </div>
      </div>
    </SidebarProvider>
  );
}
